import carouselImage1 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_1-min.jpg";
import carouselImage2 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_2-min.jpg";
import { GongLogo } from "@/components/ui/gong-logo";
import { IntegrationCard } from "@/components/ui/integration-card";
import { IntegrationFlyout } from "@/components/ui/integration-flyout";

import { GongIntegrationModal } from "./GongIntegrationModal";
import { type IntegrationConfig } from "./shared-utils";
import { useGongIntegration } from "./useGongIntegration";

export function GongIntegration() {
  const {
    integrationName,
    setIntegrationName,
    apiUrl,
    setApiUrl,
    accessKey,
    setAccessKey,
    accessKeySecret,
    setAccessKeySecret,
    isModalOpen,
    isFlyoutOpen,
    setIsFlyoutOpen,
    hasIntegrations,
    instances,
    statusLabel,
    integrationsLoading,
    createLoading,
    openModal,
    closeModal,
    deleteIntegration,
  } = useGongIntegration();

  const config: IntegrationConfig = {
    name: "Gong",
    icon: <GongLogo size={40} />,
    description:
      "Gong captures and analyzes all your customer interactions to deliver insights that help your team close more deals. Connect it to Exec to import call recordings and leverage AI-powered conversation intelligence.",
    carouselImages: [
      {
        src: carouselImage1,
        alt: "Import call recordings from Gong for analysis",
      },
      {
        src: carouselImage2,
        alt: "Analyze sales conversations with AI insights",
      },
    ],
    inputs: [
      {
        id: "integration-name",
        label: "Name",
        type: "text",
        placeholder: "e.g., Sales Team, Revenue",
        value: integrationName,
        onChange: setIntegrationName,
        required: true,
      },
      {
        id: "api-url",
        label: "API URL",
        type: "url",
        placeholder: "https://us-55616.api.gong.io",
        value: apiUrl,
        onChange: setApiUrl,
        required: true,
      },
      {
        id: "access-key",
        label: "Access Key",
        type: "text",
        placeholder: "Enter your Gong Access Key",
        value: accessKey,
        onChange: setAccessKey,
        required: true,
      },
      {
        id: "access-key-secret",
        label: "Access Key Secret",
        type: "password",
        placeholder: "Enter your Gong Access Key Secret",
        value: accessKeySecret,
        onChange: setAccessKeySecret,
        required: true,
      },
    ],
    setupDescription: (
      <div className="flex flex-col gap-3">
        <p className="text-base font-normal leading-snug text-gray-800 m-0">
          Get API credentials from your Gong workspace
        </p>
        <div className="text-base font-normal leading-relaxed">
          <ul className="list-disc ml-5 space-y-1">
            <li>
              Log in to your account at{" "}
              <a
                href="https://app.gong.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-600"
              >
                app.gong.io
              </a>
            </li>
            <li>Navigate to Admin Center → Ecosystem → API</li>
            <li>Create a new Access Key and Access Key Secret</li>
            <li>Copy your API URL, Access Key, and Access Key Secret</li>
            <li>
              Note: You need technical administrator access to create API keys
            </li>
          </ul>
        </div>
      </div>
    ),
  };

  return (
    <>
      <IntegrationFlyout
        integrationName={config.name}
        icon={config.icon}
        status={hasIntegrations ? "connected" : "disconnected"}
        statusLabel={statusLabel}
        description={config.description}
        carouselImages={config.carouselImages}
        instances={instances}
        open={isFlyoutOpen}
        onOpenChange={setIsFlyoutOpen}
        onConnect={openModal}
        onDisconnectInstance={deleteIntegration}
        loading={createLoading}
      >
        <IntegrationCard
          name={config.name}
          icon={config.icon}
          status={hasIntegrations ? "connected" : "disconnected"}
          statusLabel={statusLabel}
          onConnect={hasIntegrations ? undefined : () => setIsFlyoutOpen(true)}
          onManage={hasIntegrations ? () => setIsFlyoutOpen(true) : undefined}
          loading={integrationsLoading || createLoading}
        />
      </IntegrationFlyout>

      <GongIntegrationModal
        open={isModalOpen}
        onOpenChange={(open) => !open && closeModal()}
      />
    </>
  );
}
